import { z } from "zod";
import { authenticatedFetch } from "@/lib/authenticatedFetch";

/**
 * Thrown when a contracted route returns a body that doesn't parse
 * against its schema. Mirrors the CLI's ContractError
 * (cli/src/lib/api.ts) — separate process, separate class instance,
 * same name and shape.
 */
export class ContractError extends Error {
  constructor(
    public readonly url: string,
    public readonly issues: z.ZodIssue[],
  ) {
    const summary = issues
      .map((i) => `${i.path.join(".") || "<root>"}: ${i.message}`)
      .join("; ");
    super(`Contract violation on ${url}: ${summary}`);
    this.name = "ContractError";
  }
}

/**
 * Thrown on a non-2xx response. `serverError` carries the body's
 * `error` string when the route returned the standard
 * `{ error: string }` envelope, so callers can surface server copy
 * in toasts: `err instanceof HttpError ? err.message : fallback`.
 * `body` carries the whole parsed JSON body (when there was one) so
 * callers can inspect structured error envelopes — e.g.
 * plan-restriction bodies via planRestrictionFromHttpError().
 * Mirrors the CLI's ApiError (cli/src/lib/api.ts).
 */
export class HttpError extends Error {
  constructor(
    public readonly status: number,
    public readonly url: string,
    public readonly serverError?: string,
    public readonly body?: unknown,
  ) {
    super(serverError ?? `HTTP ${status} on ${url}`);
    this.name = "HttpError";
  }
}

function parseJsonBody(bodyText: string): unknown {
  try {
    return JSON.parse(bodyText) as unknown;
  } catch {
    // Non-JSON body — callers get the generic message.
    return undefined;
  }
}

function extractServerError(parsed: unknown): string | undefined {
  if (
    typeof parsed === "object" &&
    parsed !== null &&
    typeof (parsed as { error?: unknown }).error === "string"
  ) {
    return (parsed as { error: string }).error;
  }
  return undefined;
}

/**
 * Typed client-side fetch for contracted routes: wraps
 * authenticatedFetch, validates the success body against the schema
 * from @ripenn/api-contracts, and returns the inferred type. Schema
 * drift throws ContractError; non-OK statuses throw HttpError.
 *
 * Use bare authenticatedFetch only when the response is intentionally
 * untyped (streams, downloads, opaque proxy routes).
 */
export async function safeFetch<S extends z.ZodTypeAny>(
  schema: S,
  url: RequestInfo,
  init?: RequestInit,
): Promise<z.infer<S>> {
  const res = await authenticatedFetch(url, init);
  return validateResponse(schema, url, res);
}

/**
 * Public counterpart to `safeFetch` for unauthenticated routes
 * (e.g. the free-snapshot funnel under `/api/public/*`, hit by
 * logged-out visitors). Uses a plain `fetch` with no auth header —
 * `safeFetch`/`authenticatedFetch` throw "Authentication token is
 * missing" when there's no session, so they can't be used here — but
 * still validates the success body against the contract schema and
 * raises the same `HttpError` / `ContractError`. JSON bodies default
 * to `Content-Type: application/json`; pass `init` to override.
 */
export async function safeFetchPublic<S extends z.ZodTypeAny>(
  schema: S,
  url: RequestInfo,
  init?: RequestInit,
): Promise<z.infer<S>> {
  const headers = new Headers(init?.headers);
  if (init?.body != null && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(url, { ...init, headers });
  return validateResponse(schema, url, res);
}

async function validateResponse<S extends z.ZodTypeAny>(
  schema: S,
  url: RequestInfo,
  res: Response,
): Promise<z.infer<S>> {
  if (!res.ok) {
    const bodyText = await res.text().catch(() => "");
    const body = parseJsonBody(bodyText);
    throw new HttpError(
      res.status,
      String(url),
      extractServerError(body),
      body,
    );
  }
  const raw: unknown = await res.json();
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ContractError(String(url), parsed.error.issues);
  }
  return parsed.data;
}
